<script setup>
import { computed, onMounted, ref, unref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import MetricCard from 'dashboard/components-next/Reports/MetricCard.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import SequenceWebhookCredentialsModal from './SequenceWebhookCredentialsModal.vue';
import SequenceListRow from './SequenceListRow.vue';
import SequenceDetailPanel from './SequenceDetailPanel.vue';

const PER_PAGE = 25;
const STATUS_TABS = ['all', 'active', 'inactive'];
const TRIGGER_OPTIONS = [
  'all',
  'manual',
  'stage_change',
  'time_based',
  'condition_based',
];

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { showAlert } = useAlert();
const { isAdmin } = usePipelinePermissions();
const isAdministrator = computed(() => Boolean(unref(isAdmin)));

const prefix = key => t(`PIPELINE.SEQUENCE_DEFINITIONS.${key}`);

const records = computed(
  () =>
    store.getters['pipelineSequenceDefinitions/getSequenceDefinitions'] || []
);
const meta = computed(
  () => store.getters['pipelineSequenceDefinitions/getSequenceDefinitionsMeta']
);
const statusCounts = computed(
  () =>
    store.getters[
      'pipelineSequenceDefinitions/getSequenceDefinitionsStatusCounts'
    ]
);
const analytics = computed(
  () => store.getters['pipelineSequenceDefinitions/getSequenceAnalytics']
);
const isLoading = computed(
  () =>
    store.getters['pipelineSequenceDefinitions/isFetchingSequenceDefinitions']
);
const isLoadingDetail = computed(
  () =>
    store.getters[
      'pipelineSequenceDefinitions/isFetchingSequenceDefinitionDetail'
    ]
);
const loadError = computed(
  () => store.getters['pipelineSequenceDefinitions/getSequenceDefinitionsError']
);
const detail = computed(
  () =>
    store.getters['pipelineSequenceDefinitions/getSelectedSequenceDefinition']
);

// Filter state is seeded from the URL so a filtered list stays shareable and
// the browser's back button restores it.
const sanitized = (value, allowed, fallback) =>
  allowed.includes(value) ? value : fallback;

const statusFilter = ref(sanitized(route.query.status, STATUS_TABS, 'all'));
const triggerFilter = ref(
  sanitized(route.query.trigger, TRIGGER_OPTIONS, 'all')
);
const searchTerm = ref(route.query.q || '');
const currentPage = ref(Math.max(Number(route.query.page) || 1, 1));

const busyRecordId = ref(null);
const isConfirmingDelete = ref(false);
const deleteConfirmationRef = ref(null);
const showWebhookCredentials = ref(false);
const selectedId = ref(null);

const selectedRecord = computed(() =>
  records.value.find(record => record.id === selectedId.value)
);

const hasActiveFilters = computed(
  () =>
    statusFilter.value !== 'all' ||
    triggerFilter.value !== 'all' ||
    Boolean(searchTerm.value.trim())
);

const activeTabIndex = computed(() =>
  Math.max(STATUS_TABS.indexOf(statusFilter.value), 0)
);

// TabBar hides a falsy count, so the number is interpolated into the label —
// "Inactive (0)" is exactly the case where the count matters most.
// The FILTER_STATUS_* keys are plural: STATUS_* is the singular badge on a row.
const statusTabs = computed(() =>
  STATUS_TABS.map(status => ({
    value: status,
    label: `${prefix(`FILTER_STATUS_${status.toUpperCase()}`)} (${
      statusCounts.value[status] ?? 0
    })`,
  }))
);

const triggerOptions = computed(() =>
  TRIGGER_OPTIONS.map(value => ({
    value,
    label:
      value === 'all'
        ? prefix('FILTER_TRIGGER_ALL')
        : prefix(`TRIGGER_${value.toUpperCase()}`),
  }))
);

// SelectMenu caps its trigger button at max-w-40, so the default state shows
// the short field name instead of a truncated "All triggers".
const triggerMenuLabel = computed(() => {
  if (triggerFilter.value === 'all') return prefix('FILTER_TRIGGER_LABEL');

  return (
    triggerOptions.value.find(option => option.value === triggerFilter.value)
      ?.label || prefix('FILTER_TRIGGER_LABEL')
  );
});

const tableHeaders = computed(() => [
  prefix('TABLE_NAME'),
  prefix('TABLE_STATUS'),
  prefix('TABLE_STEPS'),
  prefix('TABLE_DURATION'),
  prefix('TABLE_TRIGGER'),
  prefix('TABLE_RUNNING'),
  '',
]);

const metrics = computed(() => {
  const data = analytics.value;
  return [
    {
      key: 'active',
      title: prefix('KPI_ACTIVE_EXECUTIONS'),
      value: data ? (data.active_sequences_count ?? 0) : '—',
      icon: 'i-lucide-zap',
      color: 'teal',
    },
    {
      key: 'completed',
      title: prefix('KPI_COMPLETED_TODAY'),
      value: data ? (data.completed_today ?? 0) : '—',
      icon: 'i-lucide-circle-check',
      color: 'blue',
    },
    {
      key: 'avg',
      title: prefix('KPI_AVG_COMPLETION'),
      value: data
        ? t('PIPELINE.SEQUENCE_DEFINITIONS.KPI_AVG_COMPLETION_VALUE', {
            n: data.avg_completion_days ?? 0,
          })
        : '—',
      icon: 'i-lucide-clock',
      color: 'amber',
    },
  ];
});

const queryParams = () => ({
  page: currentPage.value,
  perPage: PER_PAGE,
  active: statusFilter.value === 'all' ? null : statusFilter.value === 'active',
  triggerType: triggerFilter.value === 'all' ? null : triggerFilter.value,
  q: searchTerm.value.trim() || null,
});

const fetchRecords = () =>
  store.dispatch('pipelineSequenceDefinitions/fetch', queryParams());

const fetchAnalytics = () =>
  store.dispatch('pipelineSequenceDefinitions/fetchAnalytics');

// `push` for deliberate navigation (tabs, trigger, page) so back works;
// `replace` while typing, so one search does not bury the history.
const syncUrl = (method = 'push') => {
  const query = {};
  if (statusFilter.value !== 'all') query.status = statusFilter.value;
  if (triggerFilter.value !== 'all') query.trigger = triggerFilter.value;
  if (searchTerm.value.trim()) query.q = searchTerm.value.trim();
  if (currentPage.value > 1) query.page = String(currentPage.value);

  router[method]({ query }).catch(() => {});
};

const applyFilters = ({ resetPage = true, urlMethod = 'push' } = {}) => {
  if (resetPage) currentPage.value = 1;
  selectedId.value = null;
  syncUrl(urlMethod);
  return fetchRecords();
};

const onTabChanged = tab => {
  if (!tab || tab.value === statusFilter.value) return;
  statusFilter.value = tab.value;
  applyFilters();
};

const onTriggerChanged = value => {
  if (value === triggerFilter.value) return;
  triggerFilter.value = value;
  applyFilters();
};

const runSearch = useDebounceFn(() => {
  applyFilters({ urlMethod: 'replace' });
}, 300);

const onSearchInput = event => {
  searchTerm.value = event.target.value;
  runSearch();
};

const onPageChanged = page => {
  if (page === currentPage.value) return;
  currentPage.value = page;
  selectedId.value = null;
  syncUrl();
  fetchRecords();
};

const clearFilters = () => {
  statusFilter.value = 'all';
  triggerFilter.value = 'all';
  searchTerm.value = '';
  applyFilters();
};

// After any mutation the local list is stale against a server-side filter: a
// deactivated row would linger under the "Active" tab and the counters would
// drift. Refetch the page, stepping back if the last row of it was removed.
const refreshAfterMutation = async ({ removedRow = false } = {}) => {
  if (removedRow && records.value.length === 0 && currentPage.value > 1) {
    currentPage.value -= 1;
    syncUrl('replace');
  }
  await Promise.all([fetchRecords(), fetchAnalytics()]);
};

const goToNew = () => router.push({ name: 'pipeline_sequence_new' });
const sequenceEditRoute = id => ({
  name: 'pipeline_sequence_edit',
  params: { id },
});

const hasWebhookSteps = record =>
  Array.isArray(record?.steps) &&
  record.steps.some(step => step?.activity_type === 'webhook');
const canManageRecord = record =>
  isAdministrator.value || !hasWebhookSteps(record);

const goToEdit = record => {
  if (!canManageRecord(record)) return;
  router.push(sequenceEditRoute(record.id));
};

const selectRecord = record => {
  selectedId.value = selectedId.value === record.id ? null : record.id;
  if (selectedId.value) {
    store.dispatch('pipelineSequenceDefinitions/fetchOne', { id: record.id });
  }
};

const toggleActive = async record => {
  if (busyRecordId.value || !canManageRecord(record)) return;
  const nextActive = !record.active;
  busyRecordId.value = record.id;
  try {
    await store.dispatch('pipelineSequenceDefinitions/toggleActive', {
      id: record.id,
      active: nextActive,
    });
    showAlert(
      nextActive ? prefix('ACTIVATE_SUCCESS') : prefix('DEACTIVATE_SUCCESS')
    );
    await refreshAfterMutation();
  } catch {
    showAlert(t('PIPELINE.ERRORS.ERROR_GENERIC'));
  } finally {
    busyRecordId.value = null;
  }
};

const duplicate = async record => {
  if (busyRecordId.value || !canManageRecord(record)) return;
  busyRecordId.value = record.id;
  try {
    const created = await store.dispatch(
      'pipelineSequenceDefinitions/duplicate',
      { id: record.id }
    );
    showAlert(prefix('SAVE_SUCCESS'));
    if (created?.id) router.push(sequenceEditRoute(created.id));
  } catch {
    showAlert(t('PIPELINE.ERRORS.ERROR_GENERIC'));
  } finally {
    busyRecordId.value = null;
  }
};

const remove = async record => {
  if (busyRecordId.value || isConfirmingDelete.value) return;
  if (!isAdministrator.value) return;

  isConfirmingDelete.value = true;
  const confirmed = await deleteConfirmationRef.value?.showConfirmation();
  isConfirmingDelete.value = false;
  if (!confirmed || busyRecordId.value) return;

  busyRecordId.value = record.id;
  try {
    await store.dispatch('pipelineSequenceDefinitions/delete', record.id);
    showAlert(prefix('DELETE_SUCCESS'));
    if (selectedId.value === record.id) selectedId.value = null;
    await refreshAfterMutation({ removedRow: true });
  } catch {
    showAlert(t('PIPELINE.ERRORS.ERROR_GENERIC'));
  } finally {
    busyRecordId.value = null;
  }
};

// Keep the screen honest if the user navigates with back/forward.
watch(
  () => route.query,
  query => {
    const nextStatus = sanitized(query.status, STATUS_TABS, 'all');
    const nextTrigger = sanitized(query.trigger, TRIGGER_OPTIONS, 'all');
    const nextSearch = query.q || '';
    const nextPage = Math.max(Number(query.page) || 1, 1);

    const unchanged =
      nextStatus === statusFilter.value &&
      nextTrigger === triggerFilter.value &&
      nextSearch === searchTerm.value &&
      nextPage === currentPage.value;
    if (unchanged) return;

    statusFilter.value = nextStatus;
    triggerFilter.value = nextTrigger;
    searchTerm.value = nextSearch;
    currentPage.value = nextPage;
    selectedId.value = null;
    fetchRecords();
  }
);

onMounted(() => {
  fetchRecords();
  fetchAnalytics();
});
</script>

<template>
  <div class="flex flex-col flex-1 w-full min-h-0 bg-n-background">
    <!-- Header -->
    <header
      class="flex flex-col flex-shrink-0 gap-4 justify-between items-stretch py-5 px-4 border-b border-n-weak bg-n-solid-1 sm:flex-row sm:items-center sm:px-8"
    >
      <div class="flex gap-3 items-center">
        <div
          class="flex justify-center items-center w-10 h-10 bg-gradient-to-br rounded-xl shadow-md from-n-brand to-n-brand/70"
        >
          <FluentIcon icon="book" size="20" class="text-white" />
        </div>
        <div>
          <h1 class="text-lg font-semibold leading-tight text-n-slate-12">
            {{ prefix('PAGE_TITLE') }}
          </h1>
          <p class="mt-0.5 text-xs text-n-slate-10">
            {{ prefix('PAGE_SUBTITLE') }}
          </p>
        </div>
      </div>
      <div class="flex flex-col gap-2 sm:flex-row">
        <Button
          v-if="isAdministrator"
          :label="prefix('WEBHOOK_CREDENTIALS.BUTTON')"
          icon="i-lucide-lock"
          color="slate"
          variant="faded"
          size="sm"
          @click="showWebhookCredentials = true"
        />
        <Button
          :label="prefix('BTN_NEW')"
          icon="i-lucide-plus"
          color="blue"
          size="sm"
          @click="goToNew"
        />
      </div>
    </header>

    <!-- Metrics strip -->
    <div
      class="grid flex-shrink-0 gap-3 py-4 px-4 border-b border-n-weak bg-n-solid-1 sm:grid-cols-3 sm:px-8"
    >
      <MetricCard
        v-for="metric in metrics"
        :key="metric.key"
        :title="metric.title"
        :value="metric.value"
        :icon="metric.icon"
        :color="metric.color"
      />
    </div>

    <!-- Filters -->
    <div
      class="flex flex-wrap flex-shrink-0 gap-3 items-center py-3 px-4 border-b border-n-weak bg-n-solid-2 sm:px-8"
    >
      <TabBar
        :tabs="statusTabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="onTabChanged"
      />
      <SelectMenu
        :options="triggerOptions"
        :model-value="triggerFilter"
        :label="triggerMenuLabel"
        sub-menu-position="bottom"
        @update:model-value="onTriggerChanged"
      />
      <div class="flex-1 ltr:ml-auto rtl:mr-auto min-w-[12rem] max-w-sm">
        <Input
          :model-value="searchTerm"
          type="search"
          :placeholder="prefix('SEARCH_PLACEHOLDER')"
          :aria-label="prefix('SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-8 !py-1 !mb-0 bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 rtl:!pr-8',
          ]"
          class="w-full"
          @input="onSearchInput"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 top-1/2 size-4 text-n-slate-11 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>
      </div>
    </div>

    <!-- Body -->
    <div class="flex flex-1 min-h-0">
      <div class="overflow-auto flex-1 py-4 px-4 min-w-0 sm:px-8">
        <div
          v-if="isLoading"
          class="flex flex-col gap-2"
          role="status"
          aria-live="polite"
        >
          <div
            v-for="n in 6"
            :key="n"
            class="h-12 rounded-lg animate-pulse bg-n-alpha-2 motion-reduce:animate-none"
          />
          <span class="sr-only">{{ t('PIPELINE.SEQUENCES.LOADING') }}</span>
        </div>

        <div
          v-else-if="loadError"
          class="flex flex-col gap-3 justify-center items-center py-20 text-center"
          role="alert"
        >
          <FluentIcon icon="error-circle" size="28" class="text-n-ruby-10" />
          <p class="text-sm text-n-slate-11">
            {{ prefix('LOAD_ERROR') }}
          </p>
          <Button
            :label="prefix('RETRY')"
            color="blue"
            size="sm"
            @click="fetchRecords"
          />
        </div>

        <div
          v-else-if="!records.length && hasActiveFilters"
          class="flex flex-col gap-3 justify-center items-center py-20 text-center"
        >
          <p class="text-sm text-n-slate-10">
            {{
              searchTerm.trim()
                ? t('PIPELINE.SEQUENCE_DEFINITIONS.NO_RESULTS', {
                    term: searchTerm.trim(),
                  })
                : prefix('EMPTY_TITLE')
            }}
          </p>
          <Button
            :label="prefix('CLEAR_FILTERS')"
            color="slate"
            variant="faded"
            size="sm"
            @click="clearFilters"
          />
        </div>

        <div
          v-else-if="!records.length"
          class="flex flex-col justify-center items-center py-20 text-center"
        >
          <div
            class="flex justify-center items-center mb-4 w-16 h-16 rounded-2xl bg-n-alpha-2"
          >
            <FluentIcon icon="book" size="28" class="text-n-slate-9" />
          </div>
          <h3 class="mb-2 text-base font-semibold text-n-slate-12">
            {{ prefix('EMPTY_TITLE') }}
          </h3>
          <p class="mb-4 max-w-md text-sm text-n-slate-10">
            {{ prefix('EMPTY_BODY') }}
          </p>
          <Button
            :label="prefix('BTN_NEW')"
            icon="i-lucide-plus"
            color="blue"
            size="sm"
            @click="goToNew"
          />
        </div>

        <template v-else>
          <BaseTable :headers="tableHeaders" :items="records">
            <template #row>
              <SequenceListRow
                v-for="record in records"
                :key="record.id"
                :record="record"
                :is-administrator="isAdministrator"
                :is-selected="record.id === selectedId"
                :is-busy="busyRecordId === record.id"
                @select="selectRecord(record)"
                @edit="goToEdit(record)"
                @duplicate="duplicate(record)"
                @toggle-active="toggleActive(record)"
                @delete="remove(record)"
              />
            </template>
          </BaseTable>

          <PaginationFooter
            v-if="meta.total > 0"
            :current-page="currentPage"
            :total-items="meta.total"
            :items-per-page="PER_PAGE"
            class="mt-2"
            @update:current-page="onPageChanged"
          />
        </template>
      </div>

      <SequenceDetailPanel
        v-if="selectedRecord"
        :record="selectedRecord"
        :detail="detail?.id === selectedRecord.id ? detail : null"
        :is-loading-detail="isLoadingDetail"
        :is-administrator="isAdministrator"
        @close="selectedId = null"
        @edit="goToEdit(selectedRecord)"
        @duplicate="duplicate(selectedRecord)"
        @toggle-active="toggleActive(selectedRecord)"
        @delete="remove(selectedRecord)"
      />
    </div>

    <ConfirmationModal
      ref="deleteConfirmationRef"
      :title="prefix('DELETE_CONFIRM_TITLE')"
      :description="prefix('DELETE_CONFIRM_BODY')"
      :confirm-label="prefix('BTN_DELETE')"
      :cancel-label="prefix('BTN_CANCEL')"
    />

    <SequenceWebhookCredentialsModal
      v-if="isAdministrator && showWebhookCredentials"
      :show="showWebhookCredentials"
      @close="showWebhookCredentials = false"
    />
  </div>
</template>
